'use client';

import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { calculateContentSize } from "@/lib/utils/contentSize";

const DRAG_THRESHOLD_PX = 4;
const PRIMARY_BUTTON = 0;

interface UseDraggableOptions {
  initialPosition: { x: number; y: number };
  allowDragging: boolean;
  constrainingFunction?: (pos: { x: number; y: number }, size: { width: number; height: number }) => { x: number; y: number };
  size: { width: number; height: number };
  onTap?: (event: PointerEvent) => void;
}

export function useDraggable({ initialPosition, allowDragging, constrainingFunction, size, onTap }: UseDraggableOptions) {
  const isContentMeasured = size.width > 0 && size.height > 0;
  const [position, setPosition] = useState(initialPosition);
  const positionRef = useRef(initialPosition);
  const targetPositionRef = useRef(initialPosition);
  const animationFrameRef = useRef<number | null>(null);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const [hasUserDragged, setHasUserDragged] = useState(false);
  const endActiveGestureRef = useRef<(() => void) | null>(null);
  const latestOptionsRef = useRef({ size, constrainingFunction, onTap });

  useLayoutEffect(() => {
    latestOptionsRef.current = { size, constrainingFunction, onTap };
  });

  // Seed the "previous" baseline from a fresh calculation. We do not read this
  // from SizeProvider context, because the context update is scheduled by
  // Desktop's resize handler and would be one tick behind the resize event that
  // also fires this hook's handler. Reading window.* directly + the shared
  // calculateContentSize keeps the handler in a single coordinate system.
  const prevWidthContentRef = useRef(0);
  const prevHeightContentRef = useRef(0);
  const prevWindowWidthRef = useRef(0);
  const prevWindowHeightRef = useRef(0);

  useEffect(() => {
    const seed = calculateContentSize();
    prevWidthContentRef.current = seed.width;
    prevHeightContentRef.current = seed.height;
    prevWindowWidthRef.current = window.innerWidth;
    prevWindowHeightRef.current = window.innerHeight;
  }, []);

  positionRef.current = position;

  const [seededInitialPosition, setSeededInitialPosition] = useState(isContentMeasured ? initialPosition : null);
  const isAwaitingSeed = seededInitialPosition === null;
  const isFollowingNewPreset =
    !isAwaitingSeed &&
    !hasUserDragged &&
    (seededInitialPosition.x !== initialPosition.x || seededInitialPosition.y !== initialPosition.y);
  if (isContentMeasured && (isAwaitingSeed || isFollowingNewPreset)) {
    setPosition(initialPosition);
    setSeededInitialPosition(initialPosition);
  }

  const constrainPosition = (unconstrained: { x: number; y: number }) => {
    const { constrainingFunction: constrain, size: currentSize } = latestOptionsRef.current;
    return constrain ? constrain(unconstrained, currentSize) : unconstrained;
  };

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      endActiveGestureRef.current?.();
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Reposition self, based on previous position within content area.
    const onWindowResizeProjectDetails = () => {
      const prevWidthContent = prevWidthContentRef.current;
      const prevWindowWidth = prevWindowWidthRef.current;
      const prevHeightContent = prevHeightContentRef.current;
      const prevWindowHeight = prevWindowHeightRef.current;

      const prevLeftOffset = Math.floor((prevWindowWidth - prevWidthContent) / 2);
      const prevTopOffset = Math.floor((prevWindowHeight - prevHeightContent) / 2);

      const contentPercentagePositionX = prevWidthContent > 0 ? (positionRef.current.x - prevLeftOffset) / prevWidthContent : 0;
      const contentPercentagePositionY = prevHeightContent > 0 ? (positionRef.current.y - prevTopOffset) / prevHeightContent : 0;

      const { width: nextWidthContent, height: nextHeightContent } = calculateContentSize();
      const nextWindowWidth = window.innerWidth;
      const nextWindowHeight = window.innerHeight;
      const nextLeftOffset = Math.floor((nextWindowWidth - nextWidthContent) / 2);
      const nextTopOffset = Math.floor((nextWindowHeight - nextHeightContent) / 2);
      const targetPositionX = nextLeftOffset + contentPercentagePositionX * nextWidthContent;
      const targetPositionY = nextTopOffset + contentPercentagePositionY * nextHeightContent;

      setPosition(constrainPosition({ x: targetPositionX, y: targetPositionY }));

      prevWidthContentRef.current = nextWidthContent;
      prevWindowWidthRef.current = nextWindowWidth;
      prevHeightContentRef.current = nextHeightContent;
      prevWindowHeightRef.current = nextWindowHeight;
    };

    window.addEventListener('resize', onWindowResizeProjectDetails);

    return () => {
      window.removeEventListener('resize', onWindowResizeProjectDetails);
    };
  }, []);

  // Lerp window to targetPosition
  const lerpWindowPosition = () => {
    const current = positionRef.current;
    const target = targetPositionRef.current;
    const lerp = 0.15;

    const newX = current.x + (target.x - current.x) * lerp;
    const newY = current.y + (target.y - current.y) * lerp;

    const newPosition = { x: newX, y: newY };
    setPosition(newPosition);
    positionRef.current = newPosition;

    if (Math.abs(target.x - newX) > 0.1 || Math.abs(target.y - newY) > 0.1) {
      animationFrameRef.current = requestAnimationFrame(lerpWindowPosition);
    } 
    else {
      animationFrameRef.current = null;
    }
  };

  // Handle dragging
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    endActiveGestureRef.current?.();

    const capturingElement = event.currentTarget;
    const { pointerId } = event;
    const startX = event.clientX;
    const startY = event.clientY;
    const isPrimaryButton = event.button === PRIMARY_BUTTON;
    const grabOffsetX = positionRef.current.x - event.clientX;
    const grabOffsetY = positionRef.current.y - event.clientY;
    let hasCrossedDragThreshold = false;

    if (allowDragging) {
      event.preventDefault();
    }
    setIsPointerDown(true);
    capturingElement.setPointerCapture(pointerId);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) {
        return;
      }

      if (!hasCrossedDragThreshold) {
        const distanceFromStart = Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY);
        if (distanceFromStart < DRAG_THRESHOLD_PX) {
          return;
        }
        hasCrossedDragThreshold = true;
        if (allowDragging) {
          setHasUserDragged(true);
        }
      }

      if (!allowDragging) {
        return;
      }

      targetPositionRef.current = constrainPosition({
        x: moveEvent.clientX + grabOffsetX,
        y: moveEvent.clientY + grabOffsetY,
      });

      if (animationFrameRef.current === null) {
        animationFrameRef.current = requestAnimationFrame(lerpWindowPosition);
      }
    };

    const handlePointerUp = (upEvent: PointerEvent) => {
      if (upEvent.pointerId !== pointerId) {
        return;
      }

      endGesture();
      setIsPointerDown(false);

      if (!hasCrossedDragThreshold && isPrimaryButton) {
        latestOptionsRef.current.onTap?.(upEvent);
      }
    };

    const handlePointerCancel = (cancelEvent: PointerEvent) => {
      if (cancelEvent.pointerId !== pointerId) {
        return;
      }

      endGesture();
      setIsPointerDown(false);
    };

    const endGesture = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      capturingElement.removeEventListener("pointercancel", handlePointerCancel);
      capturingElement.removeEventListener("lostpointercapture", handlePointerCancel);
      if (capturingElement.hasPointerCapture(pointerId)) {
        capturingElement.releasePointerCapture(pointerId);
      }
      endActiveGestureRef.current = null;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    capturingElement.addEventListener("pointercancel", handlePointerCancel);
    capturingElement.addEventListener("lostpointercapture", handlePointerCancel);
    endActiveGestureRef.current = endGesture;
  };

  return {
    position,
    isPointerDown,
    onPointerDown,
  };
}